import React, { useMemo, useState, useEffect } from "react";
import { Stack, Box, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import rightArrow from "../assets/icons/SM_arrowRight.png";
import leftArrow from "../assets/icons/SM_arrowLeft.png";

import { MarketingPhrasesRow } from "../components/marketing/MarketingPhrasesRow";
import { CervinoHeaderText } from "../components/typography/CervinoHeaderText";
import HorizontalPillMenu from "../components/navigation/HorizontalPillMenu";

import BestSellersSection from "./products/BestSellersSection";
import BeansSection from "./products/BeansSection";
import TamalesSection from "./products/TamalesSection";
import RicesSection from "./products/RicesSection";
import {SMOtherBrandsCard} from "../components/marketing/SMOtherBrandsCard";
import SMFooter from "../components/marketing/SMFooter";
import {trackEvent} from "../utils/analytics";

const categories = [
    { label: "BEST SELLERS", value: "bestsellers" },
    { label: "BEANS", value: "beans" },
    { label: "TAMALES", value: "tamales" },
    { label: "RICES", value: "rices" },
];

const DEFAULT_TAB = "bestsellers";
const VALID_TABS = new Set(categories.map((c) => c.value));

export default function ProductsPage() {
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialTab = VALID_TABS.has(searchParams.get("tab"))
        ? searchParams.get("tab")
        : DEFAULT_TAB;

    const [selected, setSelected] = useState(initialTab);

    // If URL changes (e.g., user hits back/forward), sync state
    useEffect(() => {
        const tab = searchParams.get("tab");
        if (VALID_TABS.has(tab) && tab !== selected) {
            setSelected(tab);
        }
        if (!tab && selected !== DEFAULT_TAB) {
            // optional: keep URL clean if no tab param
            // setSelected(DEFAULT_TAB);
        }
    }, [searchParams, selected]);

    const contentMap = useMemo(
        () => ({
            bestsellers: <BestSellersSection />,
            beans: <BeansSection />,
            tamales: <TamalesSection />,
            rices: <RicesSection />,
        }),
        []
    );

    const handleChange = (val) => {
        setSelected(val);
        setSearchParams({ tab: val }, { replace: true }); // keeps it on /products
        trackEvent("select_content", {
            content_type: "product_category",
            item_id: val,
        });
    };

    const renderTabTopBlock = () => {
        if (selected === "bestsellers") {
            // EXACTLY AS IS
            return (
                <>
                    <Box paddingTop={{xs: 10, sm: 12}} marginBottom={{xs: 0, sm: -3, md: -5}}>
                        <MarketingPhrasesRow
                            leftSrc={rightArrow}
                            rightSrc={leftArrow}
                            color={theme.palette.primary.main}
                            phrases={["Real Food", "Real People", "Real Moments"]}
                        />
                    </Box>

                    <Box paddingTop={{ xs: 1 }}>
                        <CervinoHeaderText
                            color={theme.palette.primary.main}
                            headerText="BEST SELLERS"
                        />
                    </Box>
                </>
            );
        }

        if (selected === "beans") {
            return (
                <Box paddingTop={{xs: 10, sm: 12}} paddingBottom={{xs: 2, sm: 3}} sx={{px: { xs: 2, sm: 4, md: 5, lg: 1 } }}>
                    <SMOtherBrandsCard
                        backgroundSx={{
                            backgroundColor: "#81634B",
                            color: "#ffffff",
                        }}
                        showFindUsButton={false}
                        title={"SAN MIGUEL BEANS\n VS. OTHER BRANDS"}
                        features={[
                            "Non-GMO",
                            "AVOCADO OIL",
                            "High protein",
                            "gluten free",
                            "ready in 45-90 seconds",
                        ]}
                        taglineLines={[
                            "REAL BEANS,",
                            "CLEAN INGREDIENTS,",
                            "SERIOUS NUTRITION."
                        ]}
                        columns={[
                            {
                                key: "sanmiguel",
                                type: "positive",
                                header: (
                                    <Box
                                        component="img"
                                        src="/images/SM_whiteLogo.png"
                                        alt="San Miguel"
                                        sx={{
                                            height: { xs: 50, sm: 50, md: 50, lg: 60, xl: 80 },
                                            width: "auto",
                                            maxWidth: "100%",
                                            objectFit: "contain",
                                            display: "block",
                                        }}
                                    />
                                ),
                            },
                            {
                                key: "other",
                                type: "negative",
                                header: "OTHER\n BRANDS",
                            },
                        ]}
                    />
                </Box>
            );
        } else if (selected === "tamales") {
            return (
                <Box paddingTop={{xs: 10, sm: 12}} paddingBottom={{xs: 2, sm: 3}} sx={{px: { xs: 2, sm: 4, md: 5, lg: 1 } }}>
                    <SMOtherBrandsCard
                        backgroundSx={{
                            backgroundColor: "#F8A716",
                            color: "#ffffff",
                        }}
                        showFindUsButton={false}
                        title={"SAN MIGUEL TAMALES\n VS. OTHER BRANDS"}
                        features={[
                            "10g of Protein per tamal",
                            "beef tallow",
                            "gluten free",
                            "Seed oil-free",
                            "ready in 35 seconds"
                        ]}
                        taglineLines={[
                            "AUTHENTIC TAMALES,",
                            "MADE FOR",
                            "MODERN LIFESTYLES."
                        ]}
                        columns={[
                            {
                                key: "sanmiguel",
                                type: "positive",
                                header: (
                                    <Box
                                        component="img"
                                        src="/images/SM_whiteLogo.png"
                                        alt="San Miguel"
                                        sx={{
                                            height: { xs: 50, sm: 50, md: 50, lg: 60, xl: 80 },
                                            width: "auto",
                                            maxWidth: "100%",
                                            objectFit: "contain",
                                            display: "block",
                                        }}
                                    />
                                ),
                            },
                            {
                                key: "other",
                                type: "negative",
                                header: "OTHER\n BRANDS",
                            },
                        ]}
                    />
                </Box>
            );
        } else if (selected === "rices") {
            return (
                <Box paddingTop={{xs: 10, sm: 12}} paddingBottom={{xs: 2, sm: 3}} sx={{px: { xs: 2, sm: 4, md: 5, lg: 1 } }}>
                    <SMOtherBrandsCard
                        backgroundSx={{
                            backgroundColor: "#525E3B",
                            color: "#ffffff",
                        }}
                        showFindUsButton={false}
                        title={"SAN MIGUEL RICES\n VS. OTHER BRANDS"}
                        features={[
                            "cooked in bone broth",
                            "avocado oil",
                            "Up to 11g of protein",
                            "BPA-FREE",
                            "Ready in 60-90 seconds",
                        ]}
                        taglineLines={[
                            "RICE COOKED BETTER,",
                            "FOR REAL MEALS.",
                        ]}
                        columns={[
                            {
                                key: "sanmiguel",
                                type: "positive",
                                header: (
                                    <Box
                                        component="img"
                                        src="/images/SM_whiteLogo.png"
                                        alt="San Miguel"
                                        sx={{
                                            height: { xs: 50, sm: 50, md: 50, lg: 60, xl: 80 },
                                            width: "auto",
                                            maxWidth: "100%",
                                            objectFit: "contain",
                                            display: "block",
                                        }}
                                    />
                                ),
                            },
                            {
                                key: "other",
                                type: "negative",
                                header: "OTHER\n BRANDS",
                            },
                        ]}
                    />
                </Box>
            );
        }

        return null;
    };

    return (
        <Stack>

            {renderTabTopBlock()}

            <HorizontalPillMenu items={categories} value={selected} onChange={handleChange} />

            <Box sx={{ width: "100%", my: { xs: 2, sm: 3, md: 4 } }}>
                {contentMap[selected] ?? null}
            </Box>

            <Box marginTop={{ xs: 2, sm: 3 }}>
                <SMFooter></SMFooter>
            </Box>
        </Stack>
    );
}
